import { useState, useRef } from "react";
import MainLayout from "@/components/feature/MainLayout";
import InstallFlow from "./components/InstallFlow";
import FileUploadPanel from "./components/FileUploadPanel";
import TimelinePanel from "./components/TimelinePanel";
import { installApplications } from "@/mocks/systemModules";

const statusConfig: Record<string, { color: string; bg: string; step: number }> = {
  "待审核": { color: "text-gray-600", bg: "bg-gray-100", step: 0 },
  "审核中": { color: "text-amber-600", bg: "bg-amber-50", step: 1 },
  "报备中": { color: "text-teal-600", bg: "bg-teal-50", step: 2 },
  "现场勘查": { color: "text-blue-600", bg: "bg-blue-50", step: 3 },
  "施工中": { color: "text-orange-600", bg: "bg-orange-50", step: 4 },
  "验收中": { color: "text-purple-600", bg: "bg-purple-50", step: 5 },
  "已完成": { color: "text-green-600", bg: "bg-green-50", step: 6 },
};

const FLOW_STEPS = ["提交申请", "资料审核", "报备登记", "现场勘查", "施工安装", "竣工验收", "开户通气"];

export default function InstallPage() {
  const [filter, setFilter] = useState("全部");
  const [selected, setSelected] = useState<typeof installApplications[0] | null>(null);
  const [newModal, setNewModal] = useState(false);
  const [viewMode, setViewMode] = useState<"list" | "flow" | "timeline">("list");
  const [newStep, setNewStep] = useState(1);
  const [fileUploadApp, setFileUploadApp] = useState<string | null>(null);
  const [detailTab, setDetailTab] = useState<"flow" | "timeline" | "files">("timeline");

  const statuses = ["全部", "待审核", "审核中", "现场勘查", "施工中", "已完成"];
  const filtered = filter === "全部" ? installApplications : installApplications.filter(a => a.status === filter);

  const stats = {
    total: installApplications.length,
    pending: installApplications.filter(a => a.status === "待审核" || a.status === "审核中").length,
    inProgress: installApplications.filter(a => a.status === "施工中" || a.status === "现场勘查").length,
    done: installApplications.filter(a => a.status === "已完成").length,
  };

  const handleNewSubmit = () => {
    if (newStep < 3) {
      setNewStep(s => s + 1);
    } else {
      setNewModal(false);
      setNewStep(1);
    }
  };

  return (
    <MainLayout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">报装业务</h1>
            <p className="text-sm text-gray-500 mt-0.5">新装·移表·改管·增容 全流程管理（申请→审核→报备→施工→验收→开户）</p>
          </div>
          <div className="flex gap-2">
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode("list")}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${viewMode === "list" ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500"}`}
              >
                <i className="ri-list-check-2 mr-1"></i>列表
              </button>
              <button
                onClick={() => setViewMode("timeline")}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${viewMode === "timeline" ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500"}`}
              >
                <i className="ri-time-line mr-1"></i>时间轴
              </button>
              <button
                onClick={() => setViewMode("flow")}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${viewMode === "flow" ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500"}`}
              >
                <i className="ri-flow-chart mr-1"></i>流程图
              </button>
            </div>
            <button
              onClick={() => { setNewModal(true); setNewStep(1); }}
              className="flex items-center gap-2 bg-[#E8531A] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#d44a15] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i>新建报装
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: "申请总数", value: stats.total, color: "text-gray-800", bg: "bg-gray-50", icon: "ri-file-list-3-line" },
            { label: "待处理", value: stats.pending, color: "text-amber-600", bg: "bg-amber-50", icon: "ri-time-line" },
            { label: "施工中", value: stats.inProgress, color: "text-orange-600", bg: "bg-orange-50", icon: "ri-tools-line" },
            { label: "已完成", value: stats.done, color: "text-green-600", bg: "bg-green-50", icon: "ri-check-double-line" },
          ].map((s, i) => (
            <div key={i} className={`${s.bg} rounded-xl p-4 flex items-center gap-3`}>
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white">
                <i className={`${s.icon} ${s.color} text-lg`}></i>
              </div>
              <div>
                <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
                <div className="text-xs text-gray-500">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Flow Process Overview */}
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">报装业务标准流程</h3>
          <div className="flex items-center gap-0">
            {FLOW_STEPS.map((step, i) => (
              <div key={i} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
                    i < 3 ? "bg-green-500 border-green-500 text-white" :
                    i === 3 ? "bg-[#E8531A] border-[#E8531A] text-white" :
                    "bg-gray-100 border-gray-200 text-gray-400"
                  }`}>
                    {i < 3 ? <i className="ri-check-line text-xs"></i> : i + 1}
                  </div>
                  <span className={`text-[10px] mt-1 whitespace-nowrap font-medium ${i < 3 ? "text-green-600" : i === 3 ? "text-[#E8531A]" : "text-gray-400"}`}>
                    {step}
                  </span>
                </div>
                {i < FLOW_STEPS.length - 1 && (
                  <div className={`h-0.5 w-8 mb-4 ${i < 3 ? "bg-green-400" : "bg-gray-100"}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
          {statuses.map(s => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${filter === s ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
            >
              {s}
            </button>
          ))}
        </div>

        {viewMode === "list" && (
          <div className="grid grid-cols-1 gap-3">
            {filtered.map(app => {
              const cfg = statusConfig[app.status] || statusConfig["待审核"];
              const currentStep = cfg.step;
              return (
                <div key={app.id} className="bg-white rounded-xl border border-gray-100 p-5 hover:border-[#E8531A]/30 transition-colors">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center">
                        <i className="ri-tools-line text-[#E8531A] text-lg"></i>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-gray-900">{app.applicant}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.bg} ${cfg.color}`}>{app.status}</span>
                          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{app.type}</span>
                          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{app.gasType}</span>
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">{app.address}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-400">申请编号</div>
                      <div className="text-xs font-mono text-gray-600">{app.id}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-0 mb-3">
                    {FLOW_STEPS.map((step, i) => {
                      const isDone = i < currentStep;
                      const isCurrent = i === currentStep;
                      return (
                        <div key={step} className="flex items-center flex-1">
                          <div className="flex flex-col items-center">
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold transition-colors ${
                              isDone ? "bg-green-500 text-white" :
                              isCurrent ? "bg-[#E8531A] text-white" :
                              "bg-gray-100 text-gray-400"
                            }`}>
                              {isDone ? <i className="ri-check-line text-[10px]"></i> : i + 1}
                            </div>
                            <div className={`text-[9px] mt-0.5 whitespace-nowrap ${isDone ? "text-green-600" : isCurrent ? "text-[#E8531A]" : "text-gray-400"}`}>
                              {step}
                            </div>
                          </div>
                          {i < FLOW_STEPS.length - 1 && (
                            <div className={`flex-1 h-0.5 mx-0.5 mb-3.5 ${isDone ? "bg-green-400" : "bg-gray-100"}`} />
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span><i className="ri-calendar-line mr-1"></i>申请日期: {app.applyDate}</span>
                      <span><i className="ri-time-line mr-1"></i>预计完成: {app.estimatedDate}</span>
                      <span><i className="ri-phone-line mr-1"></i>{app.phone}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1.5">
                        <div className="w-24 bg-gray-100 rounded-full h-1.5">
                          <div className="bg-[#E8531A] h-1.5 rounded-full transition-all" style={{ width: `${app.progress}%` }} />
                        </div>
                        <span className="text-xs text-gray-500">{app.progress}%</span>
                      </div>
                      <button
                        onClick={() => { setFileUploadApp(app.id); }}
                        className="text-xs text-gray-500 border border-gray-200 px-3 py-1 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-upload-line mr-1"></i>文件
                      </button>
                      <button
                        onClick={() => { setSelected(app); setDetailTab("timeline"); }}
                        className="text-xs text-[#E8531A] border border-[#E8531A]/30 px-3 py-1 rounded-lg hover:bg-orange-50 cursor-pointer whitespace-nowrap"
                      >
                        进度详情
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {viewMode === "timeline" && (
          <div className="space-y-4">
            {filtered.slice(0, 3).map(app => (
              <TimelinePanel key={app.id} appId={app.id} applicant={app.applicant} />
            ))}
          </div>
        )}

        {viewMode === "flow" && (
          <div className="space-y-3">
            {filtered.slice(0, 3).map(app => {
              const cfg = statusConfig[app.status] || statusConfig["待审核"];
              return (
                <InstallFlow
                  key={app.id}
                  currentStep={cfg.step}
                  appId={app.id}
                  applicant={app.applicant}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl w-[760px] max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h3 className="text-base font-bold text-gray-900">报装详情 · {selected.id}</h3>
                <p className="text-xs text-gray-400 mt-0.5">{selected.applicant} · {selected.address}</p>
              </div>
              <button onClick={() => setSelected(null)} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>

            {/* Info grid */}
            <div className="px-6 pt-4 grid grid-cols-3 gap-3">
              {[
                { k: "申请人", v: selected.applicant },
                { k: "联系电话", v: selected.phone },
                { k: "业务类型", v: selected.type },
                { k: "用气类型", v: selected.gasType },
                { k: "申请日期", v: selected.applyDate },
                { k: "预计完成", v: selected.estimatedDate },
              ].map((row, i) => (
                <div key={i} className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-400">{row.k}</div>
                  <div className="text-sm font-medium text-gray-800 mt-0.5">{row.v}</div>
                </div>
              ))}
            </div>

            {/* Tabs */}
            <div className="flex gap-1 px-6 pt-4 border-b border-gray-100">
              {[
                { key: "timeline", label: "进度时间轴", icon: "ri-time-line" },
                { key: "flow", label: "流程图", icon: "ri-flow-chart" },
                { key: "files", label: "文件管理", icon: "ri-folder-line" },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setDetailTab(tab.key as typeof detailTab)}
                  className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                    detailTab === tab.key
                      ? "border-[#E8531A] text-[#E8531A]"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <i className={tab.icon}></i>
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {detailTab === "timeline" && (
                <TimelinePanel appId={selected.id} applicant={selected.applicant} />
              )}
              {detailTab === "flow" && (
                <InstallFlow
                  currentStep={(statusConfig[selected.status] || statusConfig["待审核"]).step}
                  appId={selected.id}
                  applicant={selected.applicant}
                />
              )}
              {detailTab === "files" && (
                <div className="space-y-3">
                  {[
                    { id: "id_card", label: "居民身份证（正反面）", required: true, status: "done", fileName: "身份证_张建国.jpg", fileSize: "1.2MB", uploadTime: "2026-04-01 09:10" },
                    { id: "property", label: "房产证/购房合同", required: true, status: "done", fileName: "房产证.pdf", fileSize: "3.5MB", uploadTime: "2026-04-01 09:12" },
                    { id: "contract", label: "用气申请合同", required: true, status: "pending" },
                    { id: "survey_report", label: "现场勘查报告", required: false, status: "pending" },
                    { id: "acceptance", label: "竣工验收报告", required: false, status: "pending" },
                  ].map(file => (
                    <div key={file.id} className={`flex items-center justify-between p-4 rounded-xl border ${file.status === "done" ? "border-green-200 bg-green-50/50" : "border-gray-100 bg-gray-50"}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${file.status === "done" ? "bg-green-100" : "bg-gray-100"}`}>
                          <i className={`${file.status === "done" ? "ri-file-check-line text-green-500" : "ri-file-upload-line text-gray-400"} text-base`}></i>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-800">{file.label}</span>
                            {file.required && <span className="text-[10px] text-red-500 bg-red-50 px-1.5 py-0.5 rounded-full">必填</span>}
                          </div>
                          {file.status === "done" && (
                            <div className="text-xs text-gray-400 mt-0.5">{file.fileName} · {file.fileSize} · {file.uploadTime}</div>
                          )}
                        </div>
                      </div>
                      {file.status === "done" ? (
                        <span className="text-xs text-green-600 bg-green-100 px-2.5 py-1 rounded-full">已上传</span>
                      ) : (
                        <button
                          onClick={() => { setSelected(null); setFileUploadApp(selected.id); }}
                          className="text-xs bg-[#E8531A] text-white px-3 py-1.5 rounded-lg cursor-pointer hover:bg-[#d44a15] whitespace-nowrap"
                        >
                          <i className="ri-upload-line mr-1"></i>上传
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* New Application Modal */}
      {newModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setNewModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-[540px]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-0 mb-5">
              {["基本信息", "用气信息", "确认提交"].map((step, i) => (
                <div key={i} className="flex items-center flex-1">
                  <div className="flex flex-col items-center">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${newStep > i + 1 ? "bg-green-500 text-white" : newStep === i + 1 ? "bg-[#E8531A] text-white" : "bg-gray-100 text-gray-400"}`}>
                      {newStep > i + 1 ? <i className="ri-check-line text-xs"></i> : i + 1}
                    </div>
                    <span className={`text-[10px] mt-1 whitespace-nowrap ${newStep === i + 1 ? "text-[#E8531A]" : "text-gray-400"}`}>{step}</span>
                  </div>
                  {i < 2 && <div className={`flex-1 h-0.5 mx-1 mb-4 ${newStep > i + 1 ? "bg-green-400" : "bg-gray-100"}`} />}
                </div>
              ))}
            </div>

            {newStep === 1 && (
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-4">填写基本信息</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">申请人姓名 *</label>
                    <input type="text" placeholder="请输入真实姓名" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">联系电话 *</label>
                    <input type="tel" placeholder="11位手机号" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">身份证号 *</label>
                    <input type="text" placeholder="18位身份证号" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">业务类型 *</label>
                    <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]">
                      <option>新装</option>
                      <option>移表</option>
                      <option>改管</option>
                      <option>增容</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs text-gray-500 mb-1 block">安装地址 *</label>
                    <input type="text" placeholder="请输入详细安装地址" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]" />
                  </div>
                </div>
              </div>
            )}

            {newStep === 2 && (
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-4">填写用气信息</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">用气类型 *</label>
                    <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]">
                      <option>居民用气</option>
                      <option>工商业用气</option>
                      <option>餐饮用气</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">预计用气量(m³/月)</label>
                    <input type="number" placeholder="预计月用气量" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">房屋性质</label>
                    <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]">
                      <option>商品房</option>
                      <option>经济适用房</option>
                      <option>自建房</option>
                      <option>商业用房</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">楼层</label>
                    <input type="number" placeholder="所在楼层" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs text-gray-500 mb-1 block">备注说明</label>
                    <textarea placeholder="其他需要说明的情况..." rows={3} maxLength={500} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A] resize-none" />
                  </div>
                </div>
              </div>
            )}

            {newStep === 3 && (
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-4">确认提交申请</h3>
                <div className="bg-orange-50 rounded-xl p-4 mb-4 border border-orange-100">
                  <div className="flex items-center gap-2 mb-3">
                    <i className="ri-information-line text-[#E8531A]"></i>
                    <span className="text-sm font-medium text-gray-800">申请信息确认</span>
                  </div>
                  <div className="space-y-2">
                    {[
                      { k: "申请人", v: "张建国" },
                      { k: "联系电话", v: "138****5678" },
                      { k: "业务类型", v: "新装" },
                      { k: "用气类型", v: "居民用气" },
                      { k: "安装地址", v: "朝阳区建国路88号3单元201" },
                    ].map((row, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="text-gray-500">{row.k}</span>
                        <span className="font-medium text-gray-800">{row.v}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500">提交后，工作人员将在1-3个工作日内完成资料审核，请保持手机畅通。</p>
                </div>
              </div>
            )}

            <div className="flex gap-2 mt-5">
              {newStep > 1 && (
                <button onClick={() => setNewStep(s => s - 1)} className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-lg text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">上一步</button>
              )}
              <button onClick={() => setNewModal(false)} className="px-4 border border-gray-200 text-gray-600 py-2.5 rounded-lg text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">取消</button>
              <button onClick={handleNewSubmit} className="flex-1 bg-[#E8531A] text-white py-2.5 rounded-lg text-sm cursor-pointer hover:bg-[#d44a15] whitespace-nowrap">
                {newStep === 3 ? "提交申请" : "下一步"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* File Upload Panel */}
      {fileUploadApp && (
        <FileUploadPanel appId={fileUploadApp} onClose={() => setFileUploadApp(null)} />
      )}
    </MainLayout>
  );
}
